import React from 'react';
import { Image } from '..';
import { Color, ImageSource } from '../types';
import classes from './index.module.scss';

/**
 * SearchBar variant options
 * - normal: Default styling
 * - outline: Outlined styling with border
 */
type Variant = 'normal' | 'outline';

/**
 * Props interface for the SearchBar component
 * Extends HTML input attributes with custom properties
 */
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
	/** Current search query text */
	query: string;
	/** Function to update the search query */
	setQuery: React.Dispatch<React.SetStateAction<string>>;
	/** Additional CSS class names */
	className?: string;
	/** Callback function when search button is clicked */
	onSearch?: () => void;
	/** Color of the search icon */
	searchIconColor?: Color;
	/** Whether the search is currently loading */
	isLoading?: boolean;
	/** Visual style variant */
	variant?: Variant;
}

/**
 * SearchBar component
 * Renders a search input field with a search button
 * Supports different variants and loading states
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const SearchBar = ({
	query,
	setQuery,
	className = '',
	variant = 'normal',
	onSearch,
	searchIconColor,
	isLoading,
	...props
}: Props) => {
	// Apply variant class if not using the default 'normal' variant
	const variantClass = variant !== 'normal' ? classes[`searchBar-${variant}`] : '';
	// Combine base class with variant class and any custom classes
	const combinedClassName = `${classes['searchBar']} ${variantClass} ${className}`;
	// Determine which search icon to use based on the color prop
	const searchIcon: ImageSource = searchIconColor === 'primary' ? 'icSearchPrimary' : 'icSearch';

	return (
		<div className={combinedClassName}>
			{/* Search input field */}
			<input
				name="query"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				className={classes['searchBar-input']}
				{...props}
			/>
			{/* Search button */}
			<a onClick={() => onSearch && onSearch()} type="submit" className={classes['searchBar-endIcon']}>
				{/* Show search icon or loading indicator based on loading state */}
				{!isLoading ? <Image src={searchIcon} /> : <span>loading indicator</span>}
			</a>
		</div>
	);
};

export default SearchBar;
